/**
 * SchoolAdminLayout.tsx  -  Shell for the school_admin portal.
 */

import { useQuery } from "@tanstack/react-query";
import {
  BarChart2,
  BookOpen,
  CalendarCheck,
  ClipboardList,
  GraduationCap,
  LayoutDashboard,
  QrCode,
  Settings,
  Users,
} from "lucide-react";
import { api } from "@/api/client";
import { useVerificationStatus } from "@/services/api";
import { PortalShell } from "./PortalShell";

function useEnrollmentBadge() {
  return useQuery({
    queryKey: ['school-admin', 'enrollment', 'pending-count'],
    queryFn: async () => {
      const data = await api.get<{ requests: { id: string }[] }>('/school-admin/enrollment-requests?status=pending')
      return data.requests.length
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export default function SchoolAdminLayout() {
  const { data: pendingEnrollments = 0 } = useEnrollmentBadge();
  const { data: verification } = useVerificationStatus();
  const unverified = verification ? !verification.verified : false;

  return (
    <PortalShell
      portalLabel="School admin"
      nav={[
        { to: "/dashboard", icon: BookOpen, label: "My teaching" },
        { to: "/school", icon: LayoutDashboard, label: "Overview", end: true },
        { to: "/school/attendance", icon: CalendarCheck, label: "Attendance" },
        { to: "/school/teachers", icon: Users, label: "Teachers" },
        { to: "/school/students", icon: GraduationCap, label: "Children" },
        { to: "/school/enrollment", icon: ClipboardList, label: "Enrolment", count: pendingEnrollments },
        { to: "/school/invite-codes", icon: QrCode, label: "Invite codes" },
        { to: "/school/analytics", icon: BarChart2, label: "Analytics" },
        { to: "/school/settings", icon: Settings, label: "Settings" },
      ]}
      banner={
        unverified ? (
          <div className="flex-shrink-0 bg-brand-yellow-soft px-6 py-2.5 text-sm text-warning-ink">
            Your school is <strong>waiting for verification</strong>. You can use everything during your trial while we complete our child-safety checks.
          </div>
        ) : undefined
      }
    />
  );
}
